import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardService } from '../services/apiService';
import Chart from 'chart.js/auto';
import './AdminDashboardPage.css';

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const depositsChartRef = useRef(null);
  const clientsChartRef = useRef(null);
  const depositsChartInstance = useRef(null);
  const clientsChartInstance = useRef(null);

  useEffect(() => {
    fetchDashboardData();

    // Cleanup charts on unmount
    return () => {
      if (depositsChartInstance.current) {
        depositsChartInstance.current.destroy();
      }
      if (clientsChartInstance.current) {
        clientsChartInstance.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (dashboardData) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        renderDepositsChart();
        renderClientsChart();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [dashboardData]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getAdminDashboard();
      setDashboardData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching admin dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const renderDepositsChart = () => {
    if (!depositsChartRef.current) return;

    // Destroy existing chart if it exists
    if (depositsChartInstance.current) {
      depositsChartInstance.current.destroy();
    }

    const ctx = depositsChartRef.current.getContext('2d');
    const labels = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const data = {
      labels: labels,
      datasets: [
        {
          label: 'Deposits',
          backgroundColor: '#013b70',
          borderColor: '#0a2c52',
          data: dashboardData?.transactions_by_month ? Object.values(dashboardData.transactions_by_month) : [],
          borderRadius: 4,
          borderWidth: 0,
        },
      ],
    };

    const config = {
      type: 'bar',
      data: data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            labels: {
              font: { size: 12 },
              color: '#555',
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: '#f0f0f0',
            },
          },
          x: {
            grid: {
              display: false,
            },
          },
        },
      },
    };

    depositsChartInstance.current = new Chart(ctx, config);
  };

  const renderClientsChart = () => {
    if (!clientsChartRef.current) return;

    // Destroy existing chart if it exists
    if (clientsChartInstance.current) {
      clientsChartInstance.current.destroy();
    }

    const ctx = clientsChartRef.current.getContext('2d');
    const labels = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const data = {
      labels: labels,
      datasets: [
        {
          label: 'Clients',
          backgroundColor: '#0a2c52',
          borderColor: '#013b70',
          data: dashboardData?.users_by_month ? Object.values(dashboardData.users_by_month) : [],
          borderRadius: 4,
          borderWidth: 0,
        },
      ],
    };

    const config = {
      type: 'bar',
      data: data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            labels: {
              font: { size: 12 },
              color: '#555',
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: '#f0f0f0',
            },
          },
          x: {
            grid: {
              display: false,
            },
          },
        },
      },
    };

    clientsChartInstance.current = new Chart(ctx, config);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Page Header */}
      <div className="page-title-box">
        <h4 className="page-title">Admin Dashboard</h4>
        <div className="breadcrumb-nav">
          <ol className="breadcrumb">
            <li><a href="/dashboard">Dashboard</a></li>
            <li className="active"><a href="/admin">Admin</a></li>
          </ol>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <i className="bx bx-exclamation-circle"></i>
          <span>{error}</span>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card stat-card--blue">
          <div className="stat-card-header">
            <div className="stat-icon"><i className="bx bx-user-circle"></i></div>
            <p className="stat-label">Total Clients</p>
          </div>
          <h4 className="stat-value">{dashboardData?.users_count || 0}</h4>
        </div>

        <div className="stat-card stat-card--teal">
          <div className="stat-card-header">
            <div className="stat-icon"><i className="bx bx-money"></i></div>
            <p className="stat-label">Total Deposits</p>
          </div>
          <h4 className="stat-value">${(dashboardData?.transactions_count || 0).toFixed(2)}</h4>
        </div>

        <div className="stat-card stat-card--purple">
          <div className="stat-card-header">
            <div className="stat-icon"><i className="bx bx-briefcase"></i></div>
            <p className="stat-label">Bonds Portfolio</p>
          </div>
          <h4 className="stat-value">{dashboardData?.bonds_portfolio_count || 0}</h4>
        </div>

        <div className="stat-card stat-card--positive">
          <div className="stat-card-header">
            <div className="stat-icon"><i className="bx bx-trending-up"></i></div>
            <p className="stat-label">Equities Portfolio</p>
          </div>
          <h4 className="stat-value">{dashboardData?.equities_portfolio_count || 0}</h4>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-grid">
        {/* Deposits Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <h5 className="chart-title">Deposits</h5>
            <div className="chart-stats">
              <div className="stat-item">
                <span className="stat-label">Total</span>
                <span className="stat-value">${dashboardData?.transactions_count?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>
          <div className="chart-container">
            <canvas ref={depositsChartRef}></canvas>
          </div>
        </div>

        {/* Clients Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <h5 className="chart-title">Clients</h5>
            <div className="chart-stats">
              <div className="stat-item">
                <span className="stat-label">Total</span>
                <span className="stat-value">{dashboardData?.users_count || 0}</span>
              </div>
            </div>
          </div>
          <div className="chart-container">
            <canvas ref={clientsChartRef}></canvas>
          </div>
        </div>
      </div>

      {/* Clients Table */}
      {dashboardData?.users && dashboardData.users.length > 0 && (
        <div className="section-card">
          <div className="section-header">
            <h5 className="section-title">Clients</h5>
            <button className="btn-primary" onClick={() => navigate('/clients/new')}>
              <i className="bx bx-plus"></i> New User
            </button>
          </div>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Email</th>
                  <th>Phone Number</th>
                  <th>Address</th>
                  <th>Zip Code</th>
                  <th>Account Number</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.users.map((user, index) => (
                  <tr key={index}>
                    <td>{user.first_name || 'N/A'}</td>
                    <td>{user.surname || 'N/A'}</td>
                    <td>{user.email || 'N/A'}</td>
                    <td>{user.telephone_number || 'N/A'}</td>
                    <td>{user.address || 'N/A'}</td>
                    <td>{user.postcode || 'N/A'}</td>
                    <td>{user.account_number || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!dashboardData || dashboardData.users?.length === 0) && (
        <div className="empty-state">
          <i className="bx bx-inbox"></i>
          <p>No clients data available</p>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
